"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const Lock_1 = __importDefault(require("@mui/icons-material/Lock"));
const material_1 = require("@mui/material");
const react_query_1 = require("@tanstack/react-query");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const lodash_1 = __importDefault(require("lodash"));
const pino_1 = __importDefault(require("pino"));
const react_1 = require("react");
const languages_1 = __importDefault(require("../../../../common/constants/languages"));
const strings_1 = require("../../../constants/strings");
const uiConstants_1 = require("../../../constants/uiConstants");
const FormLabel_1 = require("../../../material-ui-theme/components/styled/FormLabel");
const featureQuery_1 = require("../../../queries/featureQuery");
const rolesQuery_1 = require("../../../queries/rolesQuery");
const teamQuery_1 = require("../../../queries/teamQuery");
const userQuery_1 = require("../../../queries/userQuery");
const userSelectors_1 = require("../../../selectors/userSelectors");
const avatarUtils_1 = require("../../../utils/avatarUtils");
const phoneNumberUtils_1 = require("../../../utils/phoneNumberUtils");
const zustandStore_1 = require("../../../zustandStore");
const simpleButton_1 = __importDefault(require("../../atoms/button/simpleButton"));
const typeInput_1 = __importDefault(require("../../atoms/input/typeInput"));
const drawerAside_1 = __importDefault(require("../drawerAside"));
const logger = (0, pino_1.default)();
const lexico = new lexico_1.default();
const buildLanguageOptions = () => {
    if (!languages_1.default) {
        return [];
    }
    return Object.keys(languages_1.default).map(key => ({ label: languages_1.default[key], id: key }));
};
function getUserAvatar(user, file) {
    if (user?.fullName && file) {
        return (0, avatarUtils_1.getSquare)(file);
    }
    if (user?.userAvatarToRenderInUi) {
        return user?.userAvatarToRenderInUi;
    }
    return '';
}
const UserDetailsAside = ({ closeUserAside = () => ({}), openUserDetailsAside = false, }) => {
    const user = (0, zustandStore_1.useZustand)(state => state.user);
    const { data: features } = (0, react_query_1.useQuery)(featureQuery_1.featuresQuery);
    const [isAdmin, setIsAdmin] = (0, react_1.useState)(false);
    const [language, setLanguage] = (0, react_1.useState)({ id: '', language: '', label: '' });
    const [languageList, setLanguageList] = (0, react_1.useState)([]);
    const team = (0, zustandStore_1.useZustand)(state => state?.team);
    const [file, setFile] = (0, react_1.useState)(null);
    const [avatar, setAvatar] = (0, react_1.useState)(null);
    const { setTeam, setNotification } = (0, zustandStore_1.useZustand)();
    const saveUser = (0, userQuery_1.useSaveUserDataQuery)();
    const { setUser } = (0, zustandStore_1.useZustand)();
    const existingTeams = team?.teams || [];
    const teamId = team?.team?.id ? [team.team.id] : [''];
    const { data: currentTeam } = (0, teamQuery_1.useGetCurrentTeamQuery)(existingTeams, teamId);
    const role = (0, rolesQuery_1.useGetCurrentRoleForUserQuery)(user, team?.teams, team?.team?.id);
    const saveUserAvatar = (0, userQuery_1.useSaveUserAvatarQuery)();
    const name = user?.fullName || '';
    const memoizedAvatar = (0, react_1.useMemo)(() => (0, avatarUtils_1.stringAvatar)(name), [name]);
    const [disableFormButtons, setDisableFormButtons] = (0, react_1.useState)(false);
    const isHsbcCardProvider = globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP;
    const maskedPhoneNumber = (0, phoneNumberUtils_1.maskPhoneNumber)(user?.phoneDetails?.phoneCountryCode, user?.phoneDetails?.phoneNumber);
    (0, react_1.useEffect)(() => {
        setLanguageList(buildLanguageOptions());
        if (user != null && user.language) {
            setLanguage({
                id: user?.language || '',
                label: languages_1.default[user.language] || '',
            });
        }
        setIsAdmin((0, userSelectors_1.isCompanyOrSupportAdmin)(user?.roles || []));
    }, [user]);
    (0, react_1.useEffect)(() => {
        if (!isAdmin && currentTeam && currentTeam.id) {
            setTeam(currentTeam);
        }
    }, [currentTeam, isAdmin, setTeam]);
    const onFileChange = (event) => {
        if (event.target instanceof HTMLInputElement && event.target.files && event.target.files[0]) {
            if (!event.target.files[0]?.type.startsWith('image/')) {
                logger.error('Please select an image file.');
                setNotification([
                    { type: strings_1.notificationStrings.ERROR, message: strings_1.sessionUserDetailsStrings.INVALID_IMAGE_FILE_FORMAT },
                ]);
                return;
            }
            setFile(URL.createObjectURL(event.target.files[0]));
            setAvatar(event.target.files[0]);
        }
    };
    const changeUserData = (name, value) => {
        const updatedUser = {
            ...user,
            [name]: value,
        };
        setUser(updatedUser);
    };
    const changeLanguage = e => {
        const { name, value } = e.target;
        if (name === 'language') {
            setLanguage({ id: value, label: languages_1.default[value] });
        }
    };
    const onFormSubmit = () => {
        setDisableFormButtons(true);
        // updating user for sidebar
        const updatedUser = {
            ...user,
            fullName: user ? (0, userSelectors_1.fullName)(user) : '',
            initials: user?.firstName.charAt(0) || '' + user?.lastName.charAt(0),
            language: language?.id,
        };
        setUser(updatedUser);
        if (!user) {
            return;
        }
        saveUser
            .mutateAsync(user)
            .then(async () => {
            let isAvatarUploadError = false;
            if (avatar != null && user != null) {
                try {
                    await saveUserAvatar.mutateAsync({ avatar, user });
                }
                catch {
                    isAvatarUploadError = true;
                }
            }
            if (user != null) {
                setUser(user);
            }
            if (avatar) {
                if (isAvatarUploadError) {
                    setNotification([
                        {
                            type: strings_1.notificationStrings.WARNING,
                            message: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                ? strings_1.errorStrings.DEFAULT
                                : strings_1.sessionUserDetailsStrings.SAVE_AVATAR_ERROR_MESSAGE,
                        },
                    ]);
                }
                else {
                    setNotification([
                        { type: strings_1.notificationStrings.SUCCESS, message: strings_1.sessionUserDetailsStrings.SAVE_SUCCESS_MESSAGE },
                    ]);
                    closeUserAside();
                }
            }
            else {
                setNotification([
                    { type: strings_1.notificationStrings.SUCCESS, message: strings_1.sessionUserDetailsStrings.SAVE_SUCCESS_MESSAGE },
                ]);
            }
        })
            .catch(() => {
            setNotification([
                {
                    type: strings_1.notificationStrings.ERROR,
                    message: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                        ? strings_1.errorStrings.DEFAULT
                        : strings_1.sessionUserDetailsStrings.SAVE_ERROR_MESSAGE,
                },
            ]);
        })
            .finally(() => {
            resetForm();
        });
    };
    const resetForm = () => {
        setFile(null);
        setAvatar(null);
        closeUserAside();
        setDisableFormButtons(false);
    };
    const footerButtons = ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: "save-button", onClick: () => onFormSubmit(), color: uiConstants_1.UI_BUTTON_PRIMARY, style: { width: '100%' }, label: strings_1.sessionUserDetailsStrings.SAVE_BUTTON_TEXT, disabled: disableFormButtons }), (0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: "cancel-button", onClick: resetForm, color: uiConstants_1.UI_BUTTON_SECONDARY, style: { width: '100%' }, label: strings_1.sessionUserDetailsStrings.CANCEL_BUTTON_TEXT, disabled: disableFormButtons })] }));
    return ((0, jsx_runtime_1.jsx)(drawerAside_1.default, { id: 'userDetailsAside', title: lexico.tr('Edit profile'), open: openUserDetailsAside, onClose: resetForm, footerButtons: footerButtons, children: (0, jsx_runtime_1.jsxs)(material_1.Grid, { container: true, rowSpacing: 1, direction: "row", sx: {
                margin: 0,
                alignItems: 'center',
                justifyContent: 'center',
            }, children: [(0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsx)(material_1.Avatar, { alt: user?.fullName || '', "ts-variant": "profile", ...memoizedAvatar, src: getUserAvatar(user, file) }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsx)(material_1.FormControl, { sx: { pt: 1 }, children: (0, jsx_runtime_1.jsx)(material_1.TextField, { "data-testid": "user_avatar", id: "outlined-basic", label: strings_1.sessionUserDetailsStrings?.PROFILE_PIC_INPUT_LABEL_TEXT, variant: "outlined", type: "file", style: { padding: 0 }, onChange: e => onFileChange(e) }) }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: strings_1.sessionUserDetailsStrings?.FIRST_NAME_INPUT_LABEL_TEXT }), (0, jsx_runtime_1.jsx)(typeInput_1.default, { dataTestId: "firstName-input", id: "firstName", name: "firstName", value: user?.firstName || '', type: "text", onValueChange: changeUserData, placeholder: '' })] }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: strings_1.sessionUserDetailsStrings?.LAST_NAME_INPUT_LABEL_TEXT }), (0, jsx_runtime_1.jsx)(typeInput_1.default, { dataTestId: "lastName-input", id: "lastName", name: "lastName", value: user?.lastName || '', type: "text", onValueChange: changeUserData, placeholder: '' })] }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: strings_1.sessionUserDetailsStrings?.EMAIL_INPUT_LABEL_TEXT }), (0, jsx_runtime_1.jsx)(typeInput_1.default, { dataTestId: "email-input", id: "email", name: "email", value: user?.email || '', type: "text", onValueChange: changeUserData, placeholder: '', endAdornment: (0, jsx_runtime_1.jsx)(Lock_1.default, { color: "disabled" }), readOnly: true })] }) }), isHsbcCardProvider ? ((0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: strings_1.sessionUserDetailsStrings?.PHONE_INPUT_LABEL_TEXT }), (0, jsx_runtime_1.jsx)(typeInput_1.default, { dataTestId: "phoneNumber-input", id: "phoneNumber", name: "phoneNumber", placeholder: "", onValueChange: lodash_1.default.noop, value: maskedPhoneNumber || 'N/A', type: "tel", endAdornment: (0, jsx_runtime_1.jsx)(Lock_1.default, { color: "disabled" }), readOnly: true })] }) })) : null, isAdmin ? ((0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: strings_1.sessionUserDetailsStrings?.COMPANY_INPUT_LABEL_TEXT }), (0, jsx_runtime_1.jsx)(typeInput_1.default, { dataTestId: "companyName-input", id: "companyName", name: "companyName", value: user?.companyName || '', type: "text", onValueChange: changeUserData, placeholder: '', endAdornment: (0, jsx_runtime_1.jsx)(Lock_1.default, { color: "disabled" }), readOnly: true })] }) })) : (''), !isAdmin && team.team && team.team.name ? ((0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: strings_1.sessionUserDetailsStrings?.TEAM_INPUT_LABEL_TEXT }), (0, jsx_runtime_1.jsx)(typeInput_1.default, { dataTestId: "teamName-input", id: "teamName", value: team?.team?.name, type: "text", placeholder: '', endAdornment: (0, jsx_runtime_1.jsx)(Lock_1.default, { color: "disabled" }), readOnly: true, onValueChange: changeUserData })] }) })) : (''), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: strings_1.sessionUserDetailsStrings?.ROLE_INPUT_LABEL_TEXT }), (0, jsx_runtime_1.jsx)(typeInput_1.default, { dataTestId: "user_role", id: "role", value: role?.label || '', type: "text", placeholder: '', endAdornment: (0, jsx_runtime_1.jsx)(Lock_1.default, { color: "disabled" }), readOnly: true, onValueChange: changeUserData })] }) }), features && features.ENABLE_GLOBALIZATION ? ((0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: strings_1.sessionUserDetailsStrings?.LANGUAGE_INPUT_LABEL_TEXT }), (0, jsx_runtime_1.jsxs)(material_1.Select, { name: 'language', value: language?.id, onChange: changeLanguage, required: true, children: [(0, jsx_runtime_1.jsx)(material_1.MenuItem, { value: "" }, 'lang_no_value'), languageList && languageList.length > 0
                                        ? languageList?.map(lang => ((0, jsx_runtime_1.jsx)(material_1.MenuItem, { value: lang.id, children: lang.label }, lang.id)))
                                        : ''] })] }) })) : ('')] }) }));
};
exports.default = UserDetailsAside;
